import {
  Alert,
  AlertTitle,
  Box,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import CustomDialog from '~/components/customDialog'
import { TAgent } from '~/features/agents/models'
import AssistantSelectionList from '~/features/campaigns/components/assistantSelectionList'
import {
  ENonSharedCloneTargetMode,
  type TCloneDialogOptions,
  type TCloneScopeMode,
} from '~/features/campaigns/components/cloneCampaignDialog/types'
import {
  ECampaignAgentAccessScope,
  ECampaignChannel,
} from '~/features/campaigns/models'
import {
  getAgentUnsupportedChannels,
  getCampaignChannelLabel,
} from '~/features/campaigns/utils'
import rem from '~/utils/ui'

type TProps = {
  isOpen: boolean
  cloneCampaignName: string
  onCloneCampaignNameChange: (value: string) => void
  onClose: () => void
  onCancel: () => void
  onConfirm: (options: TCloneDialogOptions) => void
  isSubmitting: boolean
  isSharedCampaign: boolean
  assistants: TAgent[]
  currentAgentId: string
  /** Resolved owner assistant for non-shared campaigns (junction or `agentId`). */
  originalAssistantId: string
  campaignChannels: ECampaignChannel[]
  placeholder: string
  sharedAssistantIds?: string[]
}

const NON_SHARED_JUNCTION_CLONE = true

export default function CloneCampaignDialog({
  isOpen,
  cloneCampaignName,
  onCloneCampaignNameChange,
  onClose,
  onCancel,
  onConfirm,
  isSubmitting,
  isSharedCampaign,
  assistants,
  currentAgentId,
  originalAssistantId,
  campaignChannels,
  placeholder,
  sharedAssistantIds = [],
}: TProps) {
  const theme = useTheme()
  const [cloneScopeMode, setCloneScopeMode] =
    useState<TCloneScopeMode>('current')
  const [nonSharedCloneTargetMode, setNonSharedCloneTargetMode] = useState(
    ENonSharedCloneTargetMode.currentAssistant,
  )
  const [nonSharedShareWithOthers, setNonSharedShareWithOthers] =
    useState(false)
  const [selectedAssistantIds, setSelectedAssistantIds] = useState<string[]>([])
  const [isCurrentScopeShared, setIsCurrentScopeShared] =
    useState(isSharedCampaign)
  const [cloneJunctionContent, setCloneJunctionContent] = useState(true)

  const resolvedOriginalAssistantId = String(originalAssistantId || '').trim()
  const resolvedCurrentAgentId = String(currentAgentId || '').trim()
  const isOriginalSameAsCurrent =
    Boolean(resolvedOriginalAssistantId) &&
    Boolean(resolvedCurrentAgentId) &&
    String(resolvedOriginalAssistantId) === String(resolvedCurrentAgentId)

  useEffect(() => {
    if (!isOpen) return
    setCloneScopeMode('current')
    setNonSharedShareWithOthers(false)
    setSelectedAssistantIds([])
    setIsCurrentScopeShared(isSharedCampaign)
    setCloneJunctionContent(true)
    setNonSharedCloneTargetMode(
      isOriginalSameAsCurrent
        ? ENonSharedCloneTargetMode.originalAssistant
        : ENonSharedCloneTargetMode.currentAssistant,
    )
  }, [isOpen, isSharedCampaign, isOriginalSameAsCurrent])

  const selectableAssistants = assistants
  const getAssistantLabel = (
    assistant: TAgent | undefined,
    assistantId: string,
  ) =>
    assistant?.agentReferenceName ||
    assistant?.businessName ||
    assistant?.agentName ||
    assistantId
  const allLocationsAssistants = useMemo(
    () =>
      Array.from(
        new Set(
          sharedAssistantIds.length
            ? sharedAssistantIds.map(String).filter(Boolean)
            : [String(currentAgentId)].filter(Boolean),
        ),
      ).map((assistantId) => {
        const assistant = selectableAssistants.find(
          (agent) => String(agent.id) === assistantId,
        )
        return {
          id: assistantId,
          label: getAssistantLabel(assistant, assistantId),
          assistant,
        }
      }),
    [sharedAssistantIds, currentAgentId, selectableAssistants],
  )
  const isAllLocationsMode =
    isSharedCampaign && cloneScopeMode === 'allLocations'

  const nonSharedCloneConfirmBlocked = useMemo(() => {
    if (isSharedCampaign) {
      return false
    }
    if (!nonSharedShareWithOthers) {
      return (
        nonSharedCloneTargetMode ===
          ENonSharedCloneTargetMode.selectAssistants &&
        selectedAssistantIds.length !== 1
      )
    }
    switch (nonSharedCloneTargetMode) {
      case ENonSharedCloneTargetMode.selectAssistants:
        return selectedAssistantIds.length === 0
      case ENonSharedCloneTargetMode.currentAssistant:
        return !resolvedCurrentAgentId
      case ENonSharedCloneTargetMode.originalAssistant:
        return !(resolvedOriginalAssistantId || resolvedCurrentAgentId)
      default:
        return false
    }
  }, [
    isSharedCampaign,
    nonSharedShareWithOthers,
    nonSharedCloneTargetMode,
    selectedAssistantIds,
    resolvedOriginalAssistantId,
    resolvedCurrentAgentId,
  ])

  const shouldShowAssistantSelection = isSharedCampaign
    ? cloneScopeMode === 'selectLocations' || cloneScopeMode === 'allLocations'
    : nonSharedCloneTargetMode === ENonSharedCloneTargetMode.selectAssistants

  const currentOnlyAgentIds = useMemo(
    () => [String(currentAgentId)],
    [currentAgentId],
  )
  const targetAgentIds = useMemo(() => {
    const uniqueIds = (ids: string[]) =>
      Array.from(new Set(ids.map(String).filter(Boolean)))

    const withCurrentAgentIncluded = (ids: string[]) =>
      uniqueIds([String(currentAgentId), ...ids])

    if (!isSharedCampaign) {
      const extraWhenSharedWithOthers =
        nonSharedShareWithOthers &&
        nonSharedCloneTargetMode === ENonSharedCloneTargetMode.selectAssistants
          ? selectedAssistantIds
          : []
      switch (nonSharedCloneTargetMode) {
        case ENonSharedCloneTargetMode.originalAssistant:
          return uniqueIds([
            resolvedOriginalAssistantId || resolvedCurrentAgentId,
            ...extraWhenSharedWithOthers,
          ])
        case ENonSharedCloneTargetMode.currentAssistant:
          return uniqueIds([
            resolvedCurrentAgentId,
            ...extraWhenSharedWithOthers,
          ])
        case ENonSharedCloneTargetMode.selectAssistants:
          return nonSharedShareWithOthers
            ? withCurrentAgentIncluded(selectedAssistantIds)
            : uniqueIds(selectedAssistantIds)
        default:
          return uniqueIds(selectedAssistantIds)
      }
    }
    if (cloneScopeMode === 'allLocations') {
      return uniqueIds(allLocationsAssistants.map((assistant) => assistant.id))
    }
    if (cloneScopeMode === 'selectLocations') {
      return uniqueIds(selectedAssistantIds)
    }
    return currentOnlyAgentIds
  }, [
    isSharedCampaign,
    nonSharedCloneTargetMode,
    nonSharedShareWithOthers,
    selectedAssistantIds,
    currentOnlyAgentIds,
    cloneScopeMode,
    allLocationsAssistants,
    currentAgentId,
    resolvedOriginalAssistantId,
    resolvedCurrentAgentId,
  ])
  const isCurrentOnlySelection = useMemo(
    () =>
      targetAgentIds.length === 1 &&
      String(targetAgentIds[0]) === String(currentAgentId),
    [targetAgentIds, currentAgentId],
  )
  const isSelectAssistantsWithoutSelection =
    (isSharedCampaign &&
      cloneScopeMode === 'selectLocations' &&
      selectedAssistantIds.length === 0) ||
    nonSharedCloneConfirmBlocked

  const handleNonSharedCloneTargetModeChange = (
    mode: ENonSharedCloneTargetMode,
  ) => {
    setNonSharedCloneTargetMode(mode)
    if (mode !== ENonSharedCloneTargetMode.selectAssistants) {
      setSelectedAssistantIds([])
    }
  }

  const handleNonSharedSelectAssistantToggle = (
    assistantId: string,
    checked: boolean,
  ) => {
    if (nonSharedShareWithOthers) {
      setSelectedAssistantIds((prev) => {
        if (!checked) {
          return prev.filter((id) => id !== assistantId)
        }
        if (prev.includes(assistantId)) {
          return prev
        }
        return [...prev, assistantId]
      })
      return
    }
    if (checked) {
      setSelectedAssistantIds([assistantId])
    } else {
      setSelectedAssistantIds((prev) =>
        prev.includes(assistantId) ? [] : prev,
      )
    }
  }

  const handleSharedAssistantToggle = (assistantId: string) => {
    setSelectedAssistantIds((prev) =>
      prev.includes(assistantId)
        ? prev.filter((id) => id !== assistantId)
        : [...prev, assistantId],
    )
  }

  const handleConfirm = () => {
    if (!isSharedCampaign) {
      const agentAccessScope = nonSharedShareWithOthers
        ? ECampaignAgentAccessScope.shared
        : ECampaignAgentAccessScope.singleAgent

      onConfirm({
        agentIds: targetAgentIds,
        cloneJunctionContent: NON_SHARED_JUNCTION_CLONE,
        agentAccessScope,
      })
      return
    }

    if (cloneScopeMode === 'allLocations') {
      onConfirm({
        agentIds: targetAgentIds,
        cloneJunctionContent,
        ...(isCurrentOnlySelection
          ? {
              agentAccessScope: isCurrentScopeShared
                ? ECampaignAgentAccessScope.shared
                : ECampaignAgentAccessScope.singleAgent,
            }
          : {}),
      })
      return
    }

    if (cloneScopeMode === 'selectLocations') {
      onConfirm({
        agentIds: targetAgentIds,
        cloneJunctionContent,
        ...(isCurrentOnlySelection
          ? {
              agentAccessScope: isCurrentScopeShared
                ? ECampaignAgentAccessScope.shared
                : ECampaignAgentAccessScope.singleAgent,
            }
          : {}),
      })
      return
    }

    onConfirm({
      agentIds: targetAgentIds,
      cloneJunctionContent,
      ...(isCurrentOnlySelection
        ? {
            agentAccessScope: isCurrentScopeShared
              ? ECampaignAgentAccessScope.shared
              : ECampaignAgentAccessScope.singleAgent,
          }
        : {}),
    })
  }

  const nonSharedSegmentOptions = useMemo(() => {
    if (isOriginalSameAsCurrent) {
      return [
        {
          id: ENonSharedCloneTargetMode.originalAssistant,
          label: 'Clone for the current assistant',
        },
        {
          id: ENonSharedCloneTargetMode.selectAssistants,
          label: 'Select assistant',
        },
      ]
    }
    return [
      {
        id: ENonSharedCloneTargetMode.originalAssistant,
        label: 'Clone for the original assistant',
      },
      {
        id: ENonSharedCloneTargetMode.currentAssistant,
        label: 'Clone for the current assistant',
      },
      {
        id: ENonSharedCloneTargetMode.selectAssistants,
        label: 'Select assistant',
      },
    ]
  }, [isOriginalSameAsCurrent])

  return (
    <CustomDialog
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onCancel}
      title="Clone Campaign"
      description="Create a copy of this campaign. You can set a new name for the cloned campaign."
      isDisabled={isSubmitting || isSelectAssistantsWithoutSelection}
      shouldKeepCancelEnabled={isSelectAssistantsWithoutSelection}
      onConfirm={handleConfirm}
      cancelText="Cancel"
      confirmText="Clone"
      maxWidth={760}
    >
      <Stack
        spacing={2}
        sx={{ marginTop: rem(4), width: '100%', minWidth: rem(712) }}
      >
        <TextField
          fullWidth
          size="small"
          label="Campaign name"
          value={cloneCampaignName}
          onChange={(e) => onCloneCampaignNameChange(e.target.value)}
          placeholder={placeholder}
          disabled={isSubmitting}
          autoFocus
        />

        {!isSharedCampaign && (
          <>
            <Box
              sx={{
                display: 'inline-flex',
                width: 'fit-content',
                borderRadius: rem(8),
                border: rem(1),
                borderColor: theme.palette.tertiary1,
                borderStyle: 'solid',
                backgroundColor: theme.palette.mainBackground,
                overflow: 'hidden',
                height: rem(36),
              }}
            >
              {nonSharedSegmentOptions.map((option, index, options) => {
                const isSelected = nonSharedCloneTargetMode === option.id
                const isFirst = index === 0
                const isLast = index === options.length - 1

                return (
                  <Box
                    key={option.id}
                    component="button"
                    type="button"
                    disabled={isSubmitting}
                    onClick={() =>
                      handleNonSharedCloneTargetModeChange(option.id)
                    }
                    sx={{
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      border: rem(1),
                      borderColor: theme.palette.tertiary1,
                      borderStyle: 'solid',
                      borderTopLeftRadius: isFirst ? rem(8) : 0,
                      borderBottomLeftRadius: isFirst ? rem(8) : 0,
                      borderTopRightRadius: isLast ? rem(8) : 0,
                      borderBottomRightRadius: isLast ? rem(8) : 0,
                      px: rem(14),
                      backgroundColor: isSelected
                        ? theme.palette.tertiary1
                        : theme.palette.mainBackground,
                      color: isSelected
                        ? theme.palette.common.white
                        : theme.palette.tertiary1,
                    }}
                  >
                    <Typography variant="body2">{option.label}</Typography>
                  </Box>
                )
              })}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={nonSharedShareWithOthers}
                  onChange={(_, checked) => {
                    if (checked) {
                      setNonSharedShareWithOthers(true)
                      setNonSharedCloneTargetMode(
                        ENonSharedCloneTargetMode.selectAssistants,
                      )
                      setSelectedAssistantIds([])
                    } else {
                      setNonSharedShareWithOthers(false)
                      setSelectedAssistantIds([])
                    }
                  }}
                  disabled={isSubmitting}
                />
              }
              label="Shared with other assistants"
            />
            {nonSharedShareWithOthers && (
              <Box
                sx={{
                  borderRadius: rem(8),
                  overflow: 'hidden',
                }}
              >
                <Alert
                  severity="warning"
                  sx={{
                    borderRadius: 0,
                    margin: 0,
                  }}
                >
                  <AlertTitle>Important</AlertTitle>
                  This action cannot be undone. Once a campaign is shared, it
                  cannot be switched back to single assistant mode.
                </Alert>
              </Box>
            )}
          </>
        )}

        {isSharedCampaign && (
          <Box
            sx={{
              display: 'inline-flex',
              width: 'fit-content',
              borderRadius: rem(8),
              border: rem(1),
              borderColor: theme.palette.tertiary1,
              borderStyle: 'solid',
              backgroundColor: theme.palette.mainBackground,
              overflow: 'hidden',
              height: rem(36),
            }}
          >
            {(
              [
                { id: 'current', label: 'Clone for current Assistant' },
                {
                  id: 'allLocations',
                  label: 'Clone with all enabled Assistants',
                },
                { id: 'selectLocations', label: 'Select Assistants' },
              ] as Array<{ id: TCloneScopeMode; label: string }>
            ).map((option, index, options) => {
              const isSelected = cloneScopeMode === option.id
              const isFirst = index === 0
              const isLast = index === options.length - 1

              return (
                <Box
                  key={option.id}
                  component="button"
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setCloneScopeMode(option.id)}
                  sx={{
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    border: rem(1),
                    borderColor: theme.palette.tertiary1,
                    borderStyle: 'solid',
                    borderTopLeftRadius: isFirst ? rem(8) : 0,
                    borderBottomLeftRadius: isFirst ? rem(8) : 0,
                    borderTopRightRadius: isLast ? rem(8) : 0,
                    borderBottomRightRadius: isLast ? rem(8) : 0,
                    px: rem(14),
                    backgroundColor: isSelected
                      ? theme.palette.tertiary1
                      : theme.palette.mainBackground,
                    color: isSelected
                      ? theme.palette.common.white
                      : theme.palette.tertiary1,
                  }}
                >
                  <Typography variant="body2">{option.label}</Typography>
                </Box>
              )
            })}
          </Box>
        )}

        {isSharedCampaign && isCurrentOnlySelection && (
          <FormControlLabel
            control={
              <Switch
                checked={isCurrentScopeShared}
                onChange={(_, checked) => setIsCurrentScopeShared(checked)}
                disabled={isSubmitting}
              />
            }
            label="Shared with other assistants"
          />
        )}

        {shouldShowAssistantSelection && (
          <>
            {isSharedCampaign && (
              <FormControlLabel
                control={
                  <Switch
                    checked={cloneJunctionContent}
                    onChange={(_, checked) => setCloneJunctionContent(checked)}
                    disabled={isSubmitting}
                  />
                }
                label="Include cloned assistant metadata, knowledge, and rules."
              />
            )}
            {isSharedCampaign && <Divider />}
            <Typography variant="body1" color="text.secondary">
              Assistant List:
            </Typography>
            <AssistantSelectionList
              items={(isAllLocationsMode
                ? allLocationsAssistants
                : selectableAssistants.map((assistant) => ({
                    id: String(assistant.id),
                    label: getAssistantLabel(assistant, String(assistant.id)),
                    assistant,
                  }))
              ).map((assistantEntry) => {
                const assistantId = String(assistantEntry.id)
                const isSelected = selectedAssistantIds.includes(assistantId)
                const unsupportedChannels =
                  !assistantEntry.assistant || isAllLocationsMode
                    ? []
                    : getAgentUnsupportedChannels(
                        assistantEntry.assistant,
                        campaignChannels,
                      )
                const isChannelCompatible = unsupportedChannels.length === 0
                const isSelectionDisabled =
                  !isAllLocationsMode && !isSelected && !isChannelCompatible

                return {
                  id: assistantId,
                  label: assistantEntry.label || assistantId,
                  checked: isAllLocationsMode ? true : isSelected,
                  disabled:
                    isSubmitting || isAllLocationsMode || isSelectionDisabled,
                  tooltip:
                    !isAllLocationsMode && !isChannelCompatible
                      ? `This assistant doesn't have ${unsupportedChannels
                          .map(getCampaignChannelLabel)
                          .join(', ')} enabled.`
                      : '',
                  dimmed:
                    !isAllLocationsMode && !isSelected && !isChannelCompatible,
                }
              })}
              onToggle={(assistantId, checked) =>
                isSharedCampaign
                  ? handleSharedAssistantToggle(assistantId)
                  : handleNonSharedSelectAssistantToggle(assistantId, checked)
              }
              showSelectAll={
                isSharedCampaign
                  ? !isAllLocationsMode && cloneScopeMode === 'selectLocations'
                  : nonSharedShareWithOthers
              }
              allSelected={
                selectableAssistants
                  .filter((assistant) => {
                    const unsupportedChannels = getAgentUnsupportedChannels(
                      assistant,
                      campaignChannels,
                    )
                    return unsupportedChannels.length === 0
                  })
                  .every((assistant) =>
                    selectedAssistantIds.includes(String(assistant.id)),
                  ) &&
                selectableAssistants.some((assistant) => {
                  const unsupportedChannels = getAgentUnsupportedChannels(
                    assistant,
                    campaignChannels,
                  )
                  return unsupportedChannels.length === 0
                })
              }
              someSelected={selectedAssistantIds.some((selectedId) =>
                selectableAssistants.some(
                  (assistant) => String(assistant.id) === String(selectedId),
                ),
              )}
              disableSelectAll={isSubmitting}
              onToggleAll={(checked) => {
                if (!checked) {
                  setSelectedAssistantIds([])
                  return
                }
                const eligibleIds = selectableAssistants
                  .filter((assistant) => {
                    const unsupportedChannels = getAgentUnsupportedChannels(
                      assistant,
                      campaignChannels,
                    )
                    return unsupportedChannels.length === 0
                  })
                  .map((assistant) => String(assistant.id))
                setSelectedAssistantIds(eligibleIds)
              }}
            />
          </>
        )}
      </Stack>
    </CustomDialog>
  )
}
